"use client";

import type { LandingNavSignIn } from "@/app/components/public/landing/LandingDesktopNavigation";
import LandingSignInButton from "@/app/components/public/landing/LandingSignInButton";
import LandingTrustBadges from "@/app/components/public/landing/LandingTrustBadges";
import WatchVideoButton, { type LandingWatchAction } from "@/app/components/public/landing/WatchVideoButton";
import type { MobileHeroCopySize } from "@/src/features/landing/mobileHeroLayout";
import {
  HERO_COPY_PROVISIONAL_ATTRIBUTE,
  type DesktopHeroCopyParts,
  type DesktopHeroCopySize,
} from "@/src/features/landing/desktopHeroLayout";
import { forwardRef, useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

// What Smart Manager stands for, under the sphere on every layout. Owner
// 2026-10-17, in their words: the headline is the mission (we stop big
// companies harvesting your data), and the subhead keeps the original promise
// (your entire digital world in a single, fluid interface) and adds where it
// lives: your own encrypted Private Vault, where the agents work (the system
// works, the data doesn't). The owner cut the privacy promise from the hero as
// too much; every footer makes it, phones under the consent bar too
// (privacyPromise).
export const HERO_HEADLINE = "We stop big companies harvesting your data.";
export const HERO_SUBHEAD =
  "Your entire digital world in a single, fluid interface, with your own encrypted Private Vault where your agents work securely.";

const PART_NAMES = ["headline", "subhead", "action"] as const;

const sameParts = (a: DesktopHeroCopyParts | null, b: DesktopHeroCopyParts) =>
  a !== null && PART_NAMES.every((name) => a[name] === b[name]);

/** The copy block's rendered height at each size it has shown. */
export type HeroCopyHeights = Partial<Record<LandingHeroCopySize, number>>;

const NO_HEIGHTS: HeroCopyHeights = {};

const isCopySize = (value: string | undefined): value is LandingHeroCopySize =>
  value !== undefined && Object.hasOwn(SIZES, value);

/**
 * Counts up whenever fonts finish loading after the first render (the page's
 * own, late, or a script's first glyphs in a new locale): text measured in the
 * fallback font wraps differently, and a size that didn't fit may now.
 */
function useFontEpoch() {
  const [epoch, setEpoch] = useState(0);
  // A layout effect, so a load that finishes right after the first
  // measurement is still heard.
  useLayoutEffect(() => {
    const fonts = document.fonts;
    if (!fonts) return;
    const bump = () => setEpoch((current) => current + 1);
    fonts.addEventListener("loadingdone", bump);
    return () => fonts.removeEventListener("loadingdone", bump);
  }, []);
  return epoch;
}

/**
 * The copy block's rendered height at each size it has shown, and each of its
 * lines' (with the space above it), kept current as they reflow (locale,
 * fonts, width), so the sphere can be sized around them and a size that turns
 * out too tall can give way. Measured before the browser paints, so the layout
 * never shows a guess first. `parts` is null until the copy has rendered.
 *
 * `stage` names whatever else the heights depend on (the screen, the chrome
 * around the hero): when it changes, they start over.
 */
export function useHeroCopyHeight(stage = "") {
  // A callback ref: the block mounts and unmounts as the layout switches.
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [parts, setParts] = useState<DesktopHeroCopyParts | null>(null);
  const key = `${stage}|${useFontEpoch()}`;
  const [measured, setMeasured] = useState({ key, heights: NO_HEIGHTS });
  // The size on screen when the block was last measured.
  const measuredSize = useRef<string | undefined>(undefined);
  const measure = useCallback(() => {
    if (!element) return;
    const size = element.dataset.heroCopySize;
    // The provisional CSS can restyle the copy (a phone's compact type), so a
    // provisional height isn't the size's; it is measured again once placed.
    const provisional = element.hasAttribute(HERO_COPY_PROVISIONAL_ATTRIBUTE);
    measuredSize.current = provisional ? undefined : size;
    const height = Math.ceil(element.getBoundingClientRect().height);
    if (!provisional && isCopySize(size)) {
      setMeasured((current) => {
        const heights = current.key === key ? current.heights : NO_HEIGHTS;
        return current.key === key && heights[size] === height ? current : { key, heights: { ...heights, [size]: height } };
      });
    }
    const lines = { headline: 0, subhead: 0, action: 0 };
    for (const name of PART_NAMES) {
      const part = element.querySelector(`[data-hero-copy-part="${name}"]`);
      lines[name] = part ? Math.ceil(part.getBoundingClientRect().height) : 0;
    }
    setParts((current) => (sameParts(current, lines) ? current : lines));
  }, [element, key]);
  useLayoutEffect(() => {
    if (!element) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    // A shed line reflows without changing the block's height.
    element.querySelectorAll("[data-hero-copy-part]").forEach((part) => observer.observe(part));
    return () => observer.disconnect();
  }, [element, measure]);
  // A size that has only just rendered (one that didn't fit gave way to it) is
  // measured before it paints too, not a frame later by the observer. Reading
  // the size attribute costs no layout, so this runs after every render.
  useLayoutEffect(() => {
    if (element && element.dataset.heroCopySize !== measuredSize.current) measure();
  });
  return {
    ref: setElement,
    parts,
    heights: measured.key === key ? measured.heights : NO_HEIGHTS,
  };
}

/**
 * The compact landing's sizes and short-stage cuts (see mobileHeroLayout), and
 * the desktop's, under the desktop sphere (see desktopHeroLayout).
 */
export type LandingHeroCopySize = MobileHeroCopySize | DesktopHeroCopySize;

type CopySizing = {
  headline: string;
  subhead: string;
  /** Whether a short stage has room for the subhead; the headline always shows. */
  showsSubhead: boolean;
  /**
   * A line that doesn't show is still laid out, collapsed, so its height is
   * known and which lines fit never depends on which lines show (desktop).
   */
  measuresHidden: boolean;
  /** Space above the action row (Get started or See how it works), as padding. */
  gap: string;
  /**
   * Beside Get started, the film is its play disc alone: the shortest cut has no
   * height for a row of its own under it, and the words don't fit beside it.
   */
  filmDisc?: boolean;
  /** Under Get started, the security badges' row is left out: a short stage's first cut. */
  shedsTrust?: boolean;
};

// Space above a line is padding, never margin, so it is part of the line's measured height.
const COMPACT_CUT = {
  headline: "text-[20px] leading-[1.15]",
  subhead: "pt-1.5 text-[13px] leading-[1.4]",
  measuresHidden: false,
  gap: "pt-3",
};

const DESKTOP = {
  headline: "text-[32px] leading-[1.16]",
  subhead: "pt-3 text-[16px] leading-[1.5]",
  measuresHidden: true,
  gap: "pt-5",
};

const SIZES: Record<LandingHeroCopySize, CopySizing> = {
  phone: {
    headline: "text-[24px] leading-[1.18]",
    subhead: "pt-2.5 text-[15px] leading-[1.45]",
    showsSubhead: true,
    measuresHidden: false,
    gap: "pt-5",
  },
  tablet: {
    headline: "text-[34px] leading-[1.15]",
    subhead: "pt-3 text-[17px] leading-[1.5]",
    showsSubhead: true,
    measuresHidden: false,
    gap: "pt-7",
  },
  // Everything, tightened: what a phone inside a browser has room for.
  compact: { ...COMPACT_CUT, showsSubhead: true },
  lean: { ...COMPACT_CUT, showsSubhead: true, shedsTrust: true },
  headline: { ...COMPACT_CUT, showsSubhead: false, filmDisc: true, shedsTrust: true },
  desktop: { ...DESKTOP, showsSubhead: true },
  "desktop-headline": { ...DESKTOP, showsSubhead: false },
};

/** The line under the headline; where a short stage can't fit it, collapsed or left out. */
function CopyLine({
  line,
  shows,
  measured,
  children,
}: {
  line: "subhead";
  shows: boolean;
  measured: boolean;
  children: ReactNode;
}) {
  if (!shows && !measured) return null;
  return (
    <div
      aria-hidden={shows ? undefined : true}
      inert={shows ? undefined : true}
      data-hero-copy-line={line}
      data-hero-copy-line-shown={shows}
      className={`flex w-full flex-col items-center ${shows ? "" : "h-0 overflow-hidden opacity-0"}`}
    >
      {children}
    </div>
  );
}

type LandingHeroCopyProps = {
  t: (source: string) => string;
  size: LandingHeroCopySize;
  /** A window is open over the hero: the copy fades out, still in the document. */
  receded: boolean;
  /** "See how it works": opens the Videos window. */
  watch: LandingWatchAction;
  /**
   * Get started, then See how it works, for layouts whose header has no Get
   * started of its own. Where the nav carries it (the desktop, a tablet with
   * room), See how it works stands alone under the copy. The intro offer is
   * the nav's Pricing tab's, on every layout, so the hero never repeats it.
   */
  signIn?: LandingNavSignIn;
  /**
   * Not yet measured (the server render, or no JavaScript): placed by
   * DESKTOP_HERO_PROVISIONAL_COPY_CSS or MOBILE_HERO_PROVISIONAL_COPY_CSS
   * instead of the style's px, and visible.
   */
  provisional?: boolean;
  className?: string;
  style?: CSSProperties;
};

/** The hero's h1 (the page's only one), subhead, See how it works with the security badges and, where needed, Get started. */
const LandingHeroCopy = forwardRef<HTMLDivElement, LandingHeroCopyProps>(function LandingHeroCopy(
  { t, size, receded, watch, signIn, provisional = false, className = "", style },
  ref,
) {
  const sizes = SIZES[size];
  return (
    <div
      ref={ref}
      data-hero-copy="true"
      data-hero-copy-size={size}
      data-hero-copy-receded={receded}
      {...(provisional ? { [HERO_COPY_PROVISIONAL_ATTRIBUTE]: "" } : {})}
      className={`flex flex-col items-center text-center motion-safe:transition-opacity motion-safe:duration-300 ${receded ? "pointer-events-none opacity-0" : "pointer-events-auto opacity-100"} ${className}`}
      style={style}
    >
      <h1 data-hero-copy-part="headline" className={`text-balance font-semibold tracking-[-0.015em] text-[#EAFBFF] drop-shadow-sm ${sizes.headline}`}>
        {t(HERO_HEADLINE)}
      </h1>
      <CopyLine line="subhead" shows={sizes.showsSubhead} measured={sizes.measuresHidden}>
        <p data-hero-copy-part="subhead" className={`max-w-[34em] text-balance text-white/72 ${sizes.subhead}`}>
          {t(HERO_SUBHEAD)}
        </p>
      </CopyLine>
      {signIn ? (
        <>
          <div data-hero-copy-action="true" className={`flex w-full items-center justify-center gap-3 ${sizes.gap}`}>
            <span aria-hidden="true" className="h-px max-w-[120px] flex-1 bg-gradient-to-r from-transparent to-cyan-300/50" />
            <LandingSignInButton
              busyLabel={signIn.busyLabel}
              isBusy={signIn.isBusy}
              label={signIn.label}
              onClick={signIn.onClick}
              variant="mobile"
            />
            {sizes.filmDisc && <WatchVideoButton t={t} variant="disc" watch={watch} />}
            <span aria-hidden="true" className="h-px max-w-[120px] flex-1 bg-gradient-to-l from-transparent to-cyan-300/50" />
          </div>
          {/* Under Get started, not beside it: side by side, the two overflow a phone's column. */}
          {!sizes.filmDisc && (
            <div data-hero-copy-film="true" className="flex w-full justify-center pt-1">
              <WatchVideoButton t={t} variant="mobile" watch={watch} />
            </div>
          )}
          {/* The security standing, as on the desktop, on its own row under the film:
              beside it the two overflow a phone's column. A short stage's first cut
              leaves it out (SHED in mobileHeroLayout). */}
          {!sizes.shedsTrust && (
            <div data-hero-copy-trust="true" className="flex w-full justify-center pt-0.5">
              <LandingTrustBadges t={t} className="flex-wrap justify-center gap-y-1" />
            </div>
          )}
        </>
      ) : (
        // Every cut keeps it: the video is how the page shows what it does. The
        // security badges lead the row, and wrap above the film where it is narrow.
        <div
          data-hero-copy-part="action"
          data-hero-copy-action="true"
          className={`flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-1 ${sizes.gap}`}
        >
          <LandingTrustBadges t={t} />
          <WatchVideoButton t={t} variant="desktop" watch={watch} />
        </div>
      )}
    </div>
  );
});

export default LandingHeroCopy;
